import React, { useEffect, useRef, useState } from "react";
import { FilesetResolver, FaceLandmarker } from "@mediapipe/tasks-vision";
import { useUser } from "../contexts/userState";
import { baseUrl } from "../lib/constant";
import { useNavigate } from "react-router-dom";
import "../css/EyelashSlider.css";
import { Box, Card, CardContent, Button, Alert } from "@mui/material";

const ARCanvas = () => {
  const navigate = useNavigate();
  let fetchEyelashTemplates;
  try {
    const userContext = useUser();
    fetchEyelashTemplates = userContext.fetchEyelashTemplates;
  } catch (error) {
    console.error("Error accessing user context:", error);
    fetchEyelashTemplates = async () => ({
      status: false,
      error: "Context not available",
      statusCode: 500,
    });
  }

  const [selectedStyle, setSelectedStyle] = useState(0);
  const [selectedImage, setSelectedImage] = useState(""); // Single selection ID
  const [eyelashStyles, setEyelashStyles] = useState([]);
  const [error, setError] = useState(null);
  const [statusCode, setStatusCode] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const leftEyelashImage = useRef(null);
  const rightEyelashImage = useRef(null);
  const faceLandmarkerRef = useRef(null);
  const streamRef = useRef(null);
  const animationFrameIdRef = useRef(null);
  const sliderRef = useRef(null);
  const touchStartX = useRef(0);
  const scrollStartX = useRef(0);

  // Function to extract URL parameters and store in localStorage
  const getUrlParams = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const params = {
      user_plan_id: urlParams.get("user_plan_id"),
      token: urlParams.get("token"),
      first_name: urlParams.get("first_name"),
      last_name: urlParams.get("last_name"),
      email: urlParams.get("email"),
      mobile: urlParams.get("mobile"),
      lash_length_mm: urlParams.get("lash_length_mm"),
      inner_length: urlParams.get("inner_length"),
      center_length: urlParams.get("center_length"),
      outer_length: urlParams.get("outer_length"),
      curl_type: urlParams.get("curl_type"),
      thickness: urlParams.get("thickness"),
      id: urlParams.get("id"),
    };

    Object.keys(params).forEach((key) => {
      if (params[key]) {
        localStorage.setItem(key, params[key]);
      }
    });

    return { token: params.token, id: params.user_plan_id || params.id };
  };

  // Function to get stored parameters from localStorage
  const getStoredParams = () => {
    return {
      user_plan_id: localStorage.getItem("user_plan_id"),
      token: localStorage.getItem("token"),
      first_name: localStorage.getItem("first_name"),
      last_name: localStorage.getItem("last_name"),
      email: localStorage.getItem("email"),
      mobile: localStorage.getItem("mobile"),
      lash_length_mm: localStorage.getItem("lash_length_mm"),
      inner_length: localStorage.getItem("inner_length"),
      center_length: localStorage.getItem("center_length"),
      outer_length: localStorage.getItem("outer_length"),
      curl_type: localStorage.getItem("curl_type"),
      thickness: localStorage.getItem("thickness"),
    };
  };

  // Fetch templates on mount and select first style by default
  useEffect(() => {
    const fetchTemplates = async () => {
      const { token, id } = getUrlParams();

      if (!token || !id) {
        setError("Page Not Found");
        setStatusCode(404);
        setIsLoading(false);
        return;
      }

      try {
        const result = await fetchEyelashTemplates(token, id);
        if (result.status && result.data && Array.isArray(result.data)) {
          const apiTemplates = result.data.map((template) => ({
            id: template.id,
            name: template.name,
            left: template.left_url
              ? `${baseUrl}/${template.left_url}`
              : template.left_url,
            right: template.right_url
              ? `${baseUrl}/${template.right_url}`
              : template.right_url,
          }));

          console.log("Fetched eyelash styles:", apiTemplates);
          setEyelashStyles(apiTemplates);

          // Set the first style as selected by default
          if (apiTemplates.length > 0) {
            setSelectedStyle(0);
            setSelectedImage(apiTemplates[0].id?.toString() || "0");
          }
        } else {
          console.error("Failed to fetch templates:", result);
          setError(result.data.message || "Failed to retrieve templates.");
          setStatusCode(result.statusCode || result.status_code || 400);
        }
      } catch (err) {
        console.error("Error fetching templates:", {
          status: err.response?.status,
          url: err.response?.config?.url,
          data: err.response?.data,
        });
        setError(
          err.response?.data?.error ||
            err.response?.data?.message ||
            err.message ||
            "An unexpected error occurred."
        );
        setStatusCode(
          err.response?.status || err.response?.data?.status_code || 500
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchTemplates();
  }, [fetchEyelashTemplates]);

  // Initialize face landmarker and camera only on success
  useEffect(() => {
    if (!error && eyelashStyles.length > 0) {
      initFaceLandmarker();
    }
    return () => {
      cancelAnimationFrame(animationFrameIdRef.current);
      if (streamRef.current)
        streamRef.current.getTracks().forEach((t) => t.stop());
      if (faceLandmarkerRef.current) faceLandmarkerRef.current.close();
    };
  }, [error, eyelashStyles]);

  // Load images when selectedStyle or eyelashStyles change
  useEffect(() => {
    if (eyelashStyles.length > 0) {
      loadImages(selectedStyle);
    }
  }, [selectedStyle, eyelashStyles]);

  // Handle slider touch events and center selected style
  useEffect(() => {
    if (eyelashStyles.length > 0) {
      centerSelected(selectedStyle);
      const slider = sliderRef.current;
      if (!slider) return;

      const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
        scrollStartX.current = slider.scrollLeft;
      };

      const handleTouchMove = (e) => {
        const dx = touchStartX.current - e.touches[0].clientX;
        slider.scrollLeft = scrollStartX.current + dx;
      };

      slider.addEventListener("touchstart", handleTouchStart);
      slider.addEventListener("touchmove", handleTouchMove);

      return () => {
        slider.removeEventListener("touchstart", handleTouchStart);
        slider.removeEventListener("touchmove", handleTouchMove);
      };
    }
  }, [selectedStyle, eyelashStyles]);

  const initFaceLandmarker = async () => {
    const vision = await FilesetResolver.forVisionTasks(
      "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision/wasm"
    );
    faceLandmarkerRef.current = await FaceLandmarker.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath:
          "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task",
        delegate: "GPU",
      },
      runningMode: "VIDEO",
      numFaces: 1,
    });
    startCamera();
  };

  const startCamera = async () => {
    try {
      streamRef.current = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
          width: { ideal: 1280, max: 1920 },
          height: { ideal: 720, max: 1080 },
        },
      });
      if (videoRef.current) {
        videoRef.current.srcObject = streamRef.current;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play();
          if (canvasRef.current) {
            canvasRef.current.width = videoRef.current.videoWidth;
            canvasRef.current.height = videoRef.current.videoHeight;
          }
          detectFace();
        };
        videoRef.current.onerror = (error) => {
          console.error("Video error:", error);
        };
        videoRef.current.onplay = () => {};
      }
    } catch (error) {
      console.error("Camera error:", error);
    }
  };

  const detectFace = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const render = () => {
      if (!videoRef.current || !faceLandmarkerRef.current) {
        animationFrameIdRef.current = requestAnimationFrame(render);
        return;
      }

      if (
        videoRef.current.videoWidth === 0 ||
        videoRef.current.videoHeight === 0
      ) {
        animationFrameIdRef.current = requestAnimationFrame(render);
        return;
      }

      if (canvas.width === 0 || canvas.height === 0) {
        animationFrameIdRef.current = requestAnimationFrame(render);
        return;
      }

      try {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);

        const nowInMs = performance.now();
        const result = faceLandmarkerRef.current.detectForVideo(
          videoRef.current,
          nowInMs
        );

        if (result && result.faceLandmarks.length > 0) {
          const landmarks = result.faceLandmarks[0];
          const leftOuter = landmarks[130];
          const leftInner = landmarks[133];
          const leftTop = landmarks[159];
          const rightInner = landmarks[362];
          const rightOuter = landmarks[263];
          const rightTop = landmarks[386];
          const angle =
            Math.atan2(rightTop.y - leftTop.y, rightTop.x - leftTop.x) *
            (180 / Math.PI);

          const canvasW = canvas.width;
          const canvasH = canvas.height;
          const scaleFactor = 2.4;

          const drawEye = (img, x, y, width, height, angleDeg) => {
            if (!img) return;
            ctx.save();
            ctx.translate(x + width / 2, y + height / 2);
            ctx.rotate((angleDeg * Math.PI) / 180);
            ctx.drawImage(img, -width / 2, -height / 2, width, height);
            ctx.restore();
          };

          const leftEyeWidth =
            Math.abs(leftInner.x - leftOuter.x) * canvasW * scaleFactor;
          const leftEyeHeight = leftEyeWidth / 2;
          const leftX = leftOuter.x * canvasW - leftEyeWidth * 0.33;
          const leftY = leftTop.y * canvasH - leftEyeHeight * 0.5;

          const rightEyeWidth =
            Math.abs(rightOuter.x - rightInner.x) * canvasW * scaleFactor;
          const rightEyeHeight = rightEyeWidth / 2;
          const rightX = rightInner.x * canvasW - rightEyeWidth * 0.24;
          const rightY = rightTop.y * canvasH - rightEyeHeight * 0.5;

          drawEye(
            leftEyelashImage.current,
            leftX,
            leftY,
            leftEyeWidth,
            leftEyeHeight,
            angle
          );
          drawEye(
            rightEyelashImage.current,
            rightX,
            rightY,
            rightEyeWidth,
            rightEyeHeight,
            angle
          );
        }
      } catch (error) {
        console.warn("Face detection error:", error);
      }

      animationFrameIdRef.current = requestAnimationFrame(render);
    };

    render();
  };

  const loadImages = (index) => {
    const style = eyelashStyles[index];
    return Promise.all([
      new Promise((resolve) => {
        const img = new Image();
        img.src = style.left;
        img.onload = () => {
          leftEyelashImage.current = img;
          resolve();
        };
        img.onerror = () => {
          console.error(`Failed to load left image: ${style.left}`);
          resolve();
        };
      }),
      new Promise((resolve) => {
        const img = new Image();
        img.src = style.right;
        img.onload = () => {
          rightEyelashImage.current = img;
          resolve();
        };
        img.onerror = () => {
          console.error(`Failed to load right image: ${style.right}`);
          resolve();
        };
      }),
    ]);
  };

  const handleNext = () => {
    if (selectedImage) {
      console.log("Selected Image ID on Next:", selectedImage);
      localStorage.setItem("selectedImageId", selectedImage);
      navigate("/measurement-confirmation");
    } else {
      alert("Please select an image before proceeding.");
    }
  };

  const centerSelected = (index) => {
    const slider = sliderRef.current;
    if (!slider) return;
    const selectedItem = slider.children[index];
    if (selectedItem) {
      const leftOffset =
        selectedItem.offsetLeft -
        slider.offsetWidth / 2 +
        selectedItem.offsetWidth / 2;
      slider.scrollTo({ left: leftOffset, behavior: "smooth" });
    }
  };

  // Handle image click for single selection
  const handleImageClick = (idx, id) => {
    const value = id.toString();
    if (selectedImage === value) {
      setSelectedImage(""); // Deselect if the same image is clicked
      setSelectedStyle(0); // Reset to first style for preview
    } else {
      setSelectedImage(value); // Select new image
      setSelectedStyle(idx); // Update preview
    }
  };

  // Render error page for any error
  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="grey.50"
      >
        <Card sx={{ maxWidth: 400, width: "100%", mx: 2 }}>
          <CardContent sx={{ textAlign: "center", py: 4 }}>
            {statusCode === 404 ? (
              <>
                <Box
                  sx={{
                    fontSize: "4rem",
                    fontWeight: "bold",
                    color: "grey.500",
                    mb: 2,
                  }}
                >
                  404
                </Box>
                <Box
                  sx={{
                    fontSize: "1.5rem",
                    fontWeight: "bold",
                    color: "grey.700",
                    mb: 1,
                  }}
                >
                  Page Not Found
                </Box>
                <Box sx={{ color: "grey.600", mb: 3 }}>
                  The page you are looking for doesn't exist or has been moved.
                </Box>
              </>
            ) : (
              <>
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
                <Button
                  variant="contained"
                  onClick={() =>
                    statusCode === 401
                      ? (window.location.href = "/login")
                      : window.location.reload()
                  }
                  sx={{ mt: 2 }}
                >
                  {statusCode === 401 ? "Login Again" : "Retry"}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Render loading state
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="grey.50"
      >
        <Button variant="outlined" disabled sx={{ mr: 2 }}>
          Loading...
        </Button>
      </Box>
    );
  }

  // Render main UI only on success
  return (
    <div
      className="d-flex flex-column vh-100 bg-black text-white position-relative"
      style={{ overflowY: "hidden" }}
    >
      {/* Video/Canvas Section */}
      <div className="flex-grow-1 position-relative">
        <video
          ref={videoRef}
          className="w-100 h-100 position-absolute top-0 start-0"
          playsInline
          muted
          autoPlay
          style={{
            objectFit: "cover",
            transform: "scaleX(-1)",
          }}
        />
        <canvas
          ref={canvasRef}
          className="w-100 position-absolute top-0 start-0"
          style={{
            objectFit: "cover",
            transform: "scaleX(-1)",
            height: "100%",
            pointerEvents: "none",
          }}
        />
      </div>

      {/* Bottom Section with Eyelash Slider */}
      <div className="bg-dark border-top border-secondary">
        {/* Eyelash Thumbnail Slider */}
        <div
          className="py-2 px-2"
          style={{
            height: "120px",
            minHeight: "110px",
            overflowX: "auto",
            whiteSpace: "nowrap",
          }}
        >
          <div
            ref={sliderRef}
            className="d-flex flex-nowrap"
            style={{ scrollBehavior: "smooth" }}
          >
            {eyelashStyles.map((style, idx) => (
              <div key={style.id || idx} className="eyelash-wrapper">
                {selectedImage === (style.id || idx).toString() && (
                  <div className="checkmark">✔</div>
                )}
                <div
                  className={`eyelash-thumbnail ${
                    selectedStyle === idx ? "active" : ""
                  } ${
                    selectedImage === (style.id || idx).toString()
                      ? "selected"
                      : ""
                  }`}
                  onClick={() => handleImageClick(idx, style.id || idx)}
                  role="button"
                  aria-label={`Select eyelash style ${
                    style.name || `Style ${idx + 1}`
                  }`}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleImageClick(idx, style.id || idx);
                    }
                  }}
                >
                  <img
                    src={style.left}
                    alt={style.name || `Style ${idx + 1}`}
                    className="eyelash-img"
                  />
                </div>
                <div
                  className="eyelash-name"
                  title={style.name || `Style ${idx + 1}`}
                >
                  {style.name || `Style ${idx + 1}`}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Next Button */}
        <div className="p-2 border-top border-secondary">
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              className="btn btn-primary btn-sm"
              onClick={handleNext}
              disabled={!selectedImage}
              style={{
                fontSize: "12px",
                padding: "8px 16px",
                height: "40px",
                whiteSpace: "nowrap",
              }}
            >
              Next
            </button>
          </Box>
        </div>
      </div>
    </div>
  );
};

export default ARCanvas;